/**
 * « Sources, cibles et concurrents » — one popup, five scopes (§5.13):
 * Ce run · <moteur> · <lieu> · Ce jour · Toute la conversation (7 / 30 / 90 j).
 *
 * The page decides which scopes exist (`availableScopes`) and how each one is read; this
 * component only switches between them, caches each answer, and renders the three tabs.
 * The footnote is the page's: it says what the numbers are summed over and, beyond one
 * run, that they are minimums.
 */

import { useEffect, useState, type ReactNode } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import DomainFavicon from '../../competitors/DomainFavicon';
import { CATEGORY_CHIP_COLOR } from '../../../utils/targetColors';
import { sourceHref } from '../../../utils/sourceUrl';
import type { Folded, ScopeId } from './conversationView';

export type ConversationWindowDays = 7 | 30 | 90;

export interface ScopeOption {
  id: ScopeId;
  title: ReactNode;
  sub: string;
}

export interface ScopeResult {
  folded: Folded;
  foot: string;
}

const CELL = { borderBottom: '1px solid', borderColor: 'divider', px: 1, py: 1, fontSize: '0.8125rem' } as const;
const HEAD = { ...CELL, py: 0.75, fontSize: '0.6875rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary', whiteSpace: 'nowrap' } as const;

function MiniBar({ value, own }: { value: number; own: boolean }) {
  return (
    <Box sx={{ width: 120, height: 6, borderRadius: 3, bgcolor: '#F2F0F7', overflow: 'hidden' }}>
      <Box sx={{ width: `${Math.min(1, Math.max(0, value)) * 100}%`, height: '100%', bgcolor: own ? 'primary.main' : '#C9C4D3' }} />
    </Box>
  );
}

function Mark({ label, own }: { label: string; own: boolean }) {
  return (
    <Box aria-hidden sx={{ width: 22, height: 22, borderRadius: '6px', display: 'grid', placeItems: 'center', fontSize: 10, fontWeight: 700, flex: 'none', bgcolor: own ? 'primary.main' : '#EDEBF2', color: own ? '#fff' : 'text.secondary' }}>
      {(label[0] ?? '?').toUpperCase()}
    </Box>
  );
}

function Tag({ children }: { children: ReactNode }) {
  return (
    <Box component="span" sx={{ fontSize: 10.5, fontWeight: 600, border: '1px solid', borderColor: 'divider', borderRadius: 999, px: 0.9, color: 'text.secondary', whiteSpace: 'nowrap' }}>
      {children}
    </Box>
  );
}

export function ScopesDialog({
  open,
  onClose,
  options,
  initial = 'run',
  load,
  isTracked,
  followed,
  onFollow,
}: {
  open: boolean;
  onClose: () => void;
  options: ScopeOption[];
  initial?: ScopeId;
  /** Read one scope. `win` only matters for `all`. */
  load: (scope: ScopeId, win: ConversationWindowDays) => Promise<ScopeResult>;
  isTracked: (name: string) => boolean;
  /** Names followed during this session. */
  followed: string[];
  onFollow: (name: string, sourceDomains: string[]) => void;
}) {
  const { t } = useTranslation();
  const [scope, setScope] = useState<ScopeId>(initial);
  const [win, setWin] = useState<ConversationWindowDays>(30);
  const [tab, setTab] = useState<'brands' | 'sources' | 'targets'>('brands');
  const [cache, setCache] = useState<Record<string, ScopeResult | 'error'>>({});
  const [openUrls, setOpenUrls] = useState<Set<string>>(new Set());

  // Each opening starts on « Ce run » with a fresh cache: the run behind the page may have changed.
  useEffect(() => {
    if (open) { setScope(initial); setCache({}); setOpenUrls(new Set()); }
  }, [open, initial]);

  const key = scope === 'all' ? `all:${win}` : scope;
  const current = cache[key];
  useEffect(() => {
    if (!open || cache[key] !== undefined) return;
    let cancelled = false;
    load(scope, win)
      .then((r) => { if (!cancelled) setCache((c) => ({ ...c, [key]: r })); })
      .catch(() => { if (!cancelled) setCache((c) => ({ ...c, [key]: 'error' })); });
    return () => { cancelled = true; };
  }, [open, key, scope, win, load, cache]);

  const folded = current && current !== 'error' ? current.folded : null;
  const targets = (folded?.targets ?? []).filter((x) => x.category !== 'offre');
  const sourceDomains = (folded?.domains ?? []).filter((d) => !d.own).map((d) => d.domain);
  const maxBrand = Math.max(1, ...(folded?.brands ?? []).map((b) => b.turns));
  const maxDomain = Math.max(1, ...(folded?.domains ?? []).map((d) => d.cit));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, pr: 6 }}>
        {t('conversations.view.scopesTitle')}
        <IconButton aria-label={t('common.close')} onClick={onClose} sx={{ position: 'absolute', right: 10, top: 10 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pb: 0 }}>
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          {options.map((o) => {
            const sel = o.id === scope;
            return (
              <Box
                key={o.id}
                role="button"
                tabIndex={0}
                aria-pressed={sel}
                onClick={() => setScope(o.id)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setScope(o.id); } }}
                sx={{
                  border: '1px solid', borderColor: sel ? 'primary.main' : 'divider', bgcolor: sel ? '#F3EEFF' : 'background.paper',
                  borderRadius: '10px', px: 1.5, py: 0.9, cursor: 'pointer', textAlign: 'left',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: 13, fontWeight: 700, color: sel ? 'primary.dark' : 'text.primary' }}>
                  {o.title}
                  {o.id === 'all' && (
                    <Box sx={{ display: 'inline-flex', gap: 0.25, ml: 0.5 }} onClick={(e) => e.stopPropagation()}>
                      {([7, 30, 90] as const).map((w) => (
                        <Box
                          key={w}
                          component="button"
                          type="button"
                          onClick={() => { setWin(w); setScope('all'); }}
                          sx={{
                            all: 'unset', cursor: 'pointer', fontSize: 11, px: 0.8, py: 0.1, borderRadius: '6px', border: '1px solid',
                            borderColor: scope === 'all' && win === w ? 'primary.main' : 'divider',
                            bgcolor: scope === 'all' && win === w ? 'primary.main' : 'background.paper',
                            color: scope === 'all' && win === w ? '#fff' : 'text.primary',
                          }}
                        >
                          {t('conversations.view.winDays', { count: w })}
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
                <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{o.sub}</Typography>
              </Box>
            );
          })}
        </Box>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mt: 1.5, borderBottom: 1, borderColor: 'divider', minHeight: 40, '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 600 } }}>
          <Tab value="brands" label={t('conversations.view.tabCompetitors', { count: folded?.brands.length ?? 0 })} />
          <Tab value="sources" label={t('conversations.view.tabSources', { count: folded?.domains.length ?? 0 })} />
          <Tab value="targets" label={t('conversations.view.tabTargets', { count: targets.length })} />
        </Tabs>

        <Box sx={{ minHeight: 260, maxHeight: '52vh', overflowY: 'auto', py: 1 }}>
          {current === undefined ? (
            <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}><CircularProgress size={24} /></Box>
          ) : current === 'error' ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>{t('conversations.view.scopeError')}</Typography>
          ) : tab === 'brands' ? (
            folded!.brands.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>{t('conversations.view.scopeEmpty')}</Typography>
            ) : (
              <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <Box component="th" sx={{ ...HEAD, textAlign: 'left' }}>{t('conversations.view.colBrand')}</Box>
                    <Box component="th" sx={{ ...HEAD, textAlign: 'right' }}>{t('conversations.view.colTurns')}</Box>
                    <Box component="th" sx={HEAD} />
                    <Box component="th" sx={{ ...HEAD, textAlign: 'right' }}>{t('conversations.view.colRank')}</Box>
                    <Box component="th" sx={HEAD} />
                  </tr>
                </thead>
                <tbody>
                  {folded!.brands.map((b) => {
                    const tracked = b.own || isTracked(b.name) || followed.includes(b.name);
                    return (
                      <Box component="tr" key={b.name} sx={b.own ? { bgcolor: '#FAF7FF', '& td:first-of-type': { boxShadow: 'inset 3px 0 #7C3AED' } } : undefined}>
                        <Box component="td" sx={CELL}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
                            <Mark label={b.name} own={b.own} />{b.name}
                            {b.own ? <Tag>{t('conversations.view.you')}</Tag> : !tracked ? <Tag>{t('conversations.view.untracked')}</Tag> : null}
                          </Box>
                        </Box>
                        <Box component="td" sx={{ ...CELL, textAlign: 'right' }}><b>{b.turns}</b></Box>
                        <Box component="td" sx={CELL}><MiniBar value={b.turns / maxBrand} own={b.own} /></Box>
                        <Box component="td" sx={{ ...CELL, textAlign: 'right', color: b.meanRank == null ? 'text.disabled' : 'text.primary' }}>
                          {b.meanRank == null ? (scope === 'all' ? '' : t('conversations.view.notRanked')) : b.meanRank.toFixed(1)}
                        </Box>
                        <Box component="td" sx={{ ...CELL, textAlign: 'right' }}>
                          {!b.own && !tracked && (
                            <Button size="small" onClick={() => onFollow(b.name, sourceDomains)} sx={{ fontWeight: 700, textTransform: 'none', minWidth: 0 }}>
                              {t('conversations.view.follow')}
                            </Button>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </tbody>
              </Box>
            )
          ) : tab === 'sources' ? (
            folded!.domains.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>{t('conversations.view.scopeEmpty')}</Typography>
            ) : (
              <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <Box component="th" sx={{ ...HEAD, textAlign: 'left' }}>{t('conversations.view.colDomain')}</Box>
                    <Box component="th" sx={{ ...HEAD, textAlign: 'right' }}>{t('conversations.view.colCitations')}</Box>
                    <Box component="th" sx={HEAD} />
                  </tr>
                </thead>
                <tbody>
                  {folded!.domains.map((d) => {
                    const expanded = openUrls.has(d.domain);
                    return [
                      <Box component="tr" key={d.domain} sx={d.own ? { bgcolor: '#FAF7FF', '& td:first-of-type': { boxShadow: 'inset 3px 0 #7C3AED' } } : undefined}>
                        <Box component="td" sx={CELL}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
                            <DomainFavicon domain={d.domain} name={d.domain} size={18} />{d.domain}
                            {d.own && <Tag>{t('conversations.view.you')}</Tag>}
                            {d.urls && d.urls.length > 0 && (
                              <Box
                                component="button"
                                type="button"
                                onClick={() => setOpenUrls((s) => { const n = new Set(s); if (n.has(d.domain)) n.delete(d.domain); else n.add(d.domain); return n; })}
                                sx={{ all: 'unset', cursor: 'pointer', fontSize: 11, color: 'text.secondary', fontWeight: 500 }}
                              >
                                {expanded ? '▴' : '▾'} {t('conversations.view.urlCount', { count: d.urls.length })}
                              </Box>
                            )}
                          </Box>
                        </Box>
                        <Box component="td" sx={{ ...CELL, textAlign: 'right' }}><b>{d.cit}</b></Box>
                        <Box component="td" sx={CELL}><MiniBar value={d.cit / maxDomain} own={d.own} /></Box>
                      </Box>,
                      ...(expanded ? (d.urls ?? []).map((u) => {
                        const href = sourceHref(u.url);
                        return (
                          <Box component="tr" key={`${d.domain}|${u.url}`}>
                            <Box component="td" sx={{ ...CELL, pl: 5, fontSize: 12, color: 'text.secondary', maxWidth: 520, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {href ? <Link href={href} target="_blank" rel="noopener noreferrer" color="inherit">{u.url}</Link> : u.url}
                            </Box>
                            <Box component="td" sx={{ ...CELL, textAlign: 'right', fontSize: 12 }}>{u.cit}</Box>
                            <Box component="td" sx={CELL} />
                          </Box>
                        );
                      }) : []),
                    ];
                  })}
                </tbody>
              </Box>
            )
          ) : targets.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>{t('conversations.view.noTargets')}</Typography>
          ) : (
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <Box component="th" sx={{ ...HEAD, textAlign: 'left' }}>{t('conversations.view.colTarget')}</Box>
                  <Box component="th" sx={{ ...HEAD, textAlign: 'left' }}>{t('conversations.view.colCategory')}</Box>
                  <Box component="th" sx={{ ...HEAD, textAlign: 'right' }}>{t('conversations.view.colCitations')}</Box>
                  {scope === 'run' && <Box component="th" sx={{ ...HEAD, textAlign: 'right' }}>{t('conversations.view.colViaSource')}</Box>}
                </tr>
              </thead>
              <tbody>
                {targets.map((x) => {
                  const cc = CATEGORY_CHIP_COLOR[x.category];
                  return (
                    <tr key={x.targetId}>
                      <Box component="td" sx={{ ...CELL, fontWeight: 600 }}>{x.value}</Box>
                      <Box component="td" sx={CELL}>
                        <Box component="span" sx={{ fontSize: 11, fontWeight: 600, borderRadius: 999, px: 1, py: 0.25, bgcolor: cc?.bg ?? '#F3F4F6', color: cc?.fg ?? 'text.secondary' }}>
                          {t(`cibles.tab.${x.category}`)}
                        </Box>
                      </Box>
                      <Box component="td" sx={{ ...CELL, textAlign: 'right' }}><b>{x.cit}</b></Box>
                      {scope === 'run' && <Box component="td" sx={{ ...CELL, textAlign: 'right' }}>{x.viaSource || ''}</Box>}
                    </tr>
                  );
                })}
              </tbody>
            </Box>
          )}
        </Box>
      </DialogContent>
      <Box sx={{ px: 3, py: 1.5, borderTop: '1px solid', borderColor: 'divider', fontSize: 11.5, color: 'text.secondary', lineHeight: 1.45 }}>
        {current && current !== 'error' ? current.foot : ' '}
      </Box>
    </Dialog>
  );
}
